import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useMessages, useCreateMessage, useMarkMessagesAsRead } from "@/hooks/queries/useMessages";
import { formatImageUrl, getInitials } from "@/utils/formatters";
import { formatDistanceToNow } from "date-fns";
import { PaperAirplaneIcon, PaperClipIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Message } from "@/types";

interface ChatInterfaceProps {
  chatWithId: string;
  chatWithName: string;
  chatWithPhoto?: string;
  chatWithType?: "User" | "Company";
}

const ChatInterface = ({
  chatWithId,
  chatWithName,
  chatWithPhoto,
  chatWithType = "User",
}: ChatInterfaceProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messageText, setMessageText] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { data: messages, isLoading } = useMessages(chatWithId);
  const createMessageMutation = useCreateMessage();
  const markAsReadMutation = useMarkMessagesAsRead();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [messages]);


  // Mark messages as read when conversation is opened
  useEffect(() => {
    if (chatWithId && messages && messages.length > 0) {
      const unreadMessages = messages.filter(
        (msg) => !msg.isRead && msg.sender?.id !== user?.id
      );
      if (unreadMessages.length > 0) {
        markAsReadMutation.mutate(chatWithId);
      }
    }
  }, [chatWithId, messages, user?.id, markAsReadMutation]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !chatWithId) return;

    console.log("📤 [CHAT_INTERFACE] Sending message:", {
      messageText: messageText.trim(),
      chatWithId,
      chatWithName,
      chatWithType,
      user: user?.id,
      userRole: user?.role
    });

    try {
      await createMessageMutation.mutateAsync({
        content: { text: messageText.trim() },
        recipient: chatWithId,
        recipientModel: chatWithType,
      });
      setMessageText("");
      console.log("✅ [CHAT_INTERFACE] Message sent successfully");
    } catch (error) {
      console.error("❌ [CHAT_INTERFACE] Error sending message:", error);
      // You could add a toast notification here to show the error to the user
    }
  };

  const formatMessageTime = (date: string | Date) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {t("messaging.loadingMessages", "Loading messages...")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-sm">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {chatWithPhoto ? (
            <img
              src={formatImageUrl(chatWithPhoto)}
              alt={chatWithName}
              className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-200 dark:ring-gray-700"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                target.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          
          <div
            className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 dark:from-primary-900 dark:to-primary-800 dark:text-primary-300 flex-shrink-0 ring-2 ring-gray-200 dark:ring-gray-700 ${
              chatWithPhoto ? "hidden" : ""
            }`}
          >
            <span className="text-base sm:text-lg font-bold">
              {getInitials(chatWithName)}
            </span>
          </div>
          
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">
              {chatWithName}
            </h3>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("messaging.active", "Active")}
              </p>
            </div>
            {/* <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("messaging.online", "Online")}
              </p>
            </div> */}
          </div>
        </div>
        
        {/* Chat Actions */}
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <button className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50 dark:bg-gray-900">
        {messages && messages.length > 0 ? (
          <>
            {messages.map((message: Message) => {
              const isOwnMessage = message.sender?.id === user?.id;
              
              return (
                <div
                  key={message._id}
                  className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-sm lg:max-w-md px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl shadow-md ${
                      isOwnMessage
                        ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-br-sm"
                        : "bg-white text-gray-900 dark:bg-gray-800 dark:text-white rounded-bl-sm border border-gray-200 dark:border-gray-600"
                    }`}
                  >
                    <p className="text-sm sm:text-base break-words leading-relaxed">
                      {message.content?.text || ''}
                    </p>
                    <p
                      className={`text-[10px] sm:text-xs mt-1.5 ${
                        isOwnMessage
                          ? "text-primary-100"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {formatMessageTime(message.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="flex h-full items-center justify-center p-4">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <ChatBubbleLeftRightIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
              </div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t("messaging.noMessages", "No messages yet")}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {t("messaging.startConversation", "Start a conversation!")}
              </p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 sm:p-4 shadow-lg">
        <form onSubmit={handleSendMessage} className="flex items-end gap-2 sm:gap-3">
          <button
            type="button"
            className="p-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors hidden sm:flex mb-1"
            title={t("messaging.attachFile", "Attach file")}
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          
          <div className="flex-1 relative">
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={t("messaging.typeMessage", "Type a message...")}
              className="w-full px-4 py-3 text-sm sm:text-base border-2 border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all"
              disabled={createMessageMutation.isPending}
            />
          </div>
          
          <button
            type="submit"
            disabled={!messageText.trim() || createMessageMutation.isPending}
            className="p-3 sm:p-3.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl disabled:shadow-md active:scale-95 touch-manipulation"
            title={t("messaging.sendMessage", "Send message")}
          >
            {createMessageMutation.isPending ? (
              <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white"></div>
            ) : (
              <PaperAirplaneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
